// src/pages/Reports/TodayTours.jsx
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Toast from '../../components/Toast/Toast';
import './TodayTours.css';

const TodayTours = () => {
  const { user, isSuperAdmin } = useAuth();

  // ✅ Track selected company in state so SA can switch and we refetch
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    () => localStorage.getItem('selectedCompanyId') || ''
  );

  const [stats, setStats] = useState({ workingPeople: 0, leaves: 0 });
  const [tourEntries, setTourEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // ✅ Detect when SA changes the company picker in navbar
  useEffect(() => {
    if (!isSuperAdmin) return;

    // Poll for localStorage changes (cheap, runs every 1s)
    const interval = setInterval(() => {
      const cid = localStorage.getItem('selectedCompanyId') || '';
      if (cid !== selectedCompanyId) {
        setSelectedCompanyId(cid);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isSuperAdmin, selectedCompanyId]);

  const resolveCompanyId = useCallback(() => {
    return isSuperAdmin ? selectedCompanyId : user?.companyId;
  }, [isSuperAdmin, selectedCompanyId, user]);

  const fetchTodayStats = useCallback(async () => {
    const companyId = resolveCompanyId();
    if (!companyId) return;

    try {
      const response = await api.get('/today-tours/stats', { params: { companyId } });
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [resolveCompanyId]);

  const fetchTodayEntries = useCallback(async () => {
    const companyId = resolveCompanyId();
    if (!companyId) {
      setTourEntries([]);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/today-tours/entries', { params: { companyId } });
      if (response.data.success) {
        setTourEntries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      setToast({ message: 'Failed to fetch tour entries', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [resolveCompanyId]);

  // ✅ Refetch whenever resolved companyId changes
  useEffect(() => {
    fetchTodayStats();
    fetchTodayEntries();
  }, [fetchTodayStats, fetchTodayEntries]);

  return (
    <div className="today-tours-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <span className="breadcrumb-item">Home</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item active">Today's Tours</span>
        </div>
      </div>

      {/* ✅ Show warning if SA hasn't selected a company */}
      {isSuperAdmin && !selectedCompanyId && (
        <div style={{
          padding: '16px 20px', background: '#fff3e0',
          border: '1px solid #ffb74d', borderRadius: 8, marginBottom: 16,
          color: '#e65100', fontWeight: 600,
        }}>
          ⚠️ Please select a company from the top navbar to view today's tours.
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-header">
            <h4 className="stat-title">Working People</h4>
            <span className="stat-period">| Today</span>
          </div>
          <div className="stat-content">
            <div className="stat-icon blue">🚗</div>
            <div className="stat-value">{stats.workingPeople}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-header">
            <h4 className="stat-title">Leaves</h4>
            <span className="stat-period">| Today</span>
          </div>
          <div className="stat-content">
            <div className="stat-icon green">⏰</div>
            <div className="stat-value">{stats.leaves}</div>
          </div>
        </div>
      </div>

      <div className="tour-entries-section">
        <div className="section-header">
          <h4 className="section-title">Tour Entries</h4>
          <span className="section-period">| Today</span>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="loading">Loading tour entries...</div>
          ) : (
            <table className="tour-entries-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Ecode</th>
                  <th>Name</th>
                  <th>Destination</th>
                </tr>
              </thead>
              <tbody>
                {tourEntries.length > 0 ? (
                  tourEntries.map((entry) => (
                    <tr key={entry.sr}>
                      <td>{entry.sr}</td>
                      <td>{entry.employeeCode}</td>
                      <td>{entry.name}</td>
                      <td>{entry.destination}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">
                      {isSuperAdmin && !selectedCompanyId
                        ? 'Select a company to view tours'
                        : 'No tour entries for today'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TodayTours;